// Portal tools counted on the admin Activity page. A route belongs to a tool when its path equals
// one of the tool's prefixes or continues it with a "/" (so /p never matches /profile).
// Shared by the client tracker (plugins/activity-tracking.client.ts) and the server endpoint.

export const ACTIVITY_TOOLS = {
  dashboard: { label: 'Dashboard', prefixes: ['/dashboard'] },
  masterclass: { label: 'Learn · Masterclass', prefixes: ['/learn', '/masterclass/onboarding'] },
  salesTraining: { label: 'Learn · Market & Sales', prefixes: ['/sales-training'] },
  vault: { label: 'Learn · Developer Vault', prefixes: ['/developer-vault', '/vault', '/training'] },
  ask: { label: 'Ask AI', prefixes: ['/ask'] },
  deals: { label: 'Deals', prefixes: ['/deals'] },
  projects: { label: 'Projects', prefixes: ['/explore', '/projects', '/properties', '/p'] },
  developers: { label: 'Developers', prefixes: ['/developers'] },
  resale: { label: 'Resale', prefixes: ['/resale'] },
  marketing: { label: 'Marketing', prefixes: ['/marketing'] },
  news: { label: 'News', prefixes: ['/news'] },
  referrals: { label: 'Referrals', prefixes: ['/referrals'] },
  earnings: { label: 'Earnings', prefixes: ['/payments-history'] },
  tiers: { label: 'Tiers', prefixes: ['/rankings'] },
  profile: { label: 'Profile', prefixes: ['/profile'] },
  services: { label: 'Services', prefixes: ['/services', '/my-services'] },
} as const satisfies Record<string, { label: string; prefixes: readonly string[] }>;

export type ActivityTool = keyof typeof ACTIVITY_TOOLS;

export const ACTIVITY_KINDS = ['login', 'tool'] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

// The same tool opened again within this window counts once (browsing inside Projects, reloads).
export const ACTIVITY_TOOL_REPEAT_MS = 30 * 60 * 1000;

export const ACTIVITY_PERIOD_DAYS = [7, 30, 90] as const;
export const DEFAULT_ACTIVITY_PERIOD_DAYS = 30;

export const isActivityTool = (value: unknown): value is ActivityTool =>
  typeof value === 'string' && Object.hasOwn(ACTIVITY_TOOLS, value);

export const activityToolLabel = (tool: string): string => (isActivityTool(tool) ? ACTIVITY_TOOLS[tool].label : tool);

const matchesPrefix = (path: string, prefix: string) => path === prefix || path.startsWith(`${prefix}/`);

// `path` must already have its locale prefix stripped.
export const toolForPath = (path: string): ActivityTool | null => {
  const clean = path.split(/[?#]/)[0] ?? '';
  for (const [tool, def] of Object.entries(ACTIVITY_TOOLS)) {
    if (def.prefixes.some((prefix) => matchesPrefix(clean, prefix))) return tool as ActivityTool;
  }
  return null;
};
